import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Redux slice for bank account information
 * Manages borrower bank account details for payment processing
 */
export interface BankState {
  bankName: string | null;
  bankCode: string | null;
  accountNumber: string | null;
  accountType: string | null;
  accountHolderName: string | null;
  swiftCode: string | null;
  bankAddress: string | null;
  bankCountry: string | null;
  currency: string | null;
}

const initialState: BankState = {
  bankName: null,
  bankCode: null,
  accountNumber: null,
  accountType: null,
  accountHolderName: null,
  swiftCode: null,
  bankAddress: null,
  bankCountry: null,
  currency: null,
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof BankState>(field: K) => {
  return (state: BankState, action: PayloadAction<BankState[K]>) => {
    state[field] = action.payload;
  };
};

const BorbankSlice = createSlice({
  name: "BorBank",
  initialState,
  reducers: {
    // Bank identification
    setBankNameRedux: createFieldSetter("bankName"),
    setBankCodeRedux: createFieldSetter("bankCode"),
    setSwiftCodeReduct: createFieldSetter("swiftCode"),

    // Account details
    setAccountNoRedux: createFieldSetter("accountNumber"),
    setAccountTypeRedux: createFieldSetter("accountType"),
    setAccountHolderNameRedux: createFieldSetter("accountHolderName"),

    // Bank address details
    setBankAddressRedux: createFieldSetter("bankAddress"),
    setBankCountryRedux: createFieldSetter("bankCountry"),
    setCurrencyRedux: createFieldSetter("currency"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateBankState: (state, action: PayloadAction<Partial<BankState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetBankState: () => initialState,
  },
});

export const {
  setBankNameRedux,
  setBankCodeRedux,
  setAccountNoRedux,
  setAccountHolderNameRedux,
  setSwiftCodeReduct,
  setAccountTypeRedux,
  setBankAddressRedux,
  setBankCountryRedux,
  setCurrencyRedux,
  hydrateBankState,
  resetBankState,
} = BorbankSlice.actions;

export default BorbankSlice.reducer;
